'use client'

import { Heart, ShoppingCart, Star } from 'lucide-react'
import { useState } from 'react'
import type { Product } from '@/types/product'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useCartStore } from '@/stores/cart'
import { useWishlistStore } from '@/stores/wishlist'
import { useCurrencyStore } from '@/stores/currency'
import { Card } from '@/components/ui/card'

interface ProductCardProps {
  product: Product
  onQuickView?: (product: Product) => void
  showBuyNow?: boolean
}

export default function ProductCard({ product, onQuickView, showBuyNow = true }: ProductCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [addedToCart, setAddedToCart] = useState(false)
  
  const addItem = useCartStore(state => state.addItem)
  const { isInWishlist, toggleItem } = useWishlistStore()
  const getSymbol = useCurrencyStore(state => state.getSymbol)
  const convert = useCurrencyStore(state => state.convert)
  const inWishlist = isInWishlist(product.id)

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    addItem({
      product_id: product.id,
      title: product.title,
      image: product.images[0]?.url || '',
      price: product.pricing.current_price,
      original_price: product.pricing.original_price,
      vendor_name: product.vendor.name,
    })
    
    setAddedToCart(true)
    setTimeout(() => setAddedToCart(false), 2000)
  }

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    toggleItem(product.id)
  }

  const handleCardClick = () => {
    if (onQuickView) {
      onQuickView(product)
    }
  }

  return (
    <Card className="group overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer flex flex-col h-full">
      <div onClick={handleCardClick} className="flex flex-col h-full">
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden bg-muted">
          {!imageLoaded && (
            <div className="absolute inset-0 bg-muted animate-pulse" />
          )}
          <img
            src={product.images[0]?.url || ''}
            alt={product.title}
            className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            loading="lazy"
          />
          
          {/* Discount Badge - with rectangular background */}
          {product.pricing.discount_percentage > 0 && (
            <div className="absolute top-2 left-2">
              <div className="bg-accent text-accent-foreground px-2 py-1 rounded text-xs font-bold">
                -{product.pricing.discount_percentage}%
              </div>
            </div>
          )}
          
          {/* Wishlist Button - Fixed for light mode with solid background */}
          <button
            onClick={handleToggleWishlist}
            className="absolute top-2 right-2 p-2 rounded-full bg-card border border-border hover:bg-accent hover:text-accent-foreground transition-colors shadow-sm"
            aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart
              className={`h-4 w-4 ${inWishlist ? 'fill-current text-destructive' : 'text-foreground'}`}
            />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-1">
          <div className="space-y-2 flex-1">
          {/* Vendor Badge */}
          {product.vendor.is_verified && (
            <Badge variant="outline" className="text-xs">
              {product.badges.includes('choice') ? 'BotTola Choice' : product.vendor.name}
            </Badge>
          )}

          {/* Title */}
          <h3 className="text-sm font-medium text-foreground line-clamp-2 min-h-[2.5rem]">
            {product.title}
          </h3>

          {/* Rating */}
          <div className="flex items-center gap-1">
            <div className="flex">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={`h-3 w-3 ${
                    i < Math.floor(product.rating.average)
                      ? 'fill-current text-yellow-500'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">({product.rating.count})</span>
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold text-brand-orange">
              {getSymbol()}{convert(product.pricing.current_price).toLocaleString()}
            </span>
            {product.pricing.discount_percentage > 0 && (
              <span className="text-sm text-muted-foreground line-through">
                {getSymbol()}{convert(product.pricing.original_price).toLocaleString()}
              </span>
            )}
          </div>

          {/* Shipping Info */}
          <div className="text-xs text-muted-foreground">
            {product.shipping.free_shipping ? (
              <span className="text-success font-medium">Free Shipping</span>
            ) : (
              <span>Shipping: {getSymbol()}{convert(product.shipping.cost).toLocaleString()}</span>
            )}
          </div>

          </div>
          
          {/* Action Buttons - Now at bottom with mt-auto */}
          <div className="flex gap-2 pt-2 mt-auto">
            {showBuyNow && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={(e) => {
                  e.stopPropagation()
                  // Handle buy now
                }}
              >
                Buy Now
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              className={`flex-1 ${showBuyNow ? '' : 'w-full'}`}
              onClick={handleAddToCart}
              disabled={product.stock_status === 'out_of_stock'}
            >
              {addedToCart ? (
                <>✓ Added</>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4 mr-1" />
                  Add
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}
